// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useAudienceSegments, AudienceSegment } from "@/hooks/useAudienceSegments";
import { Calculator, Loader2, ArrowLeft } from "lucide-react";
import { CheckboxDropdown } from "@/components/ui/checkbox-dropdown";
import { supabase } from "@/integrations/supabase/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface SegmentEditViewProps {
  segment?: AudienceSegment | null;
  onBack: () => void;
  onSuccess: () => void;
}

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];


export const SegmentEditView: React.FC<SegmentEditViewProps> = ({
  segment,
  onBack,
  onSuccess,
}) => {
  const { toast } = useToast();
  const { createSegment, updateSegment, calculateRecipients } = useAudienceSegments();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [estimatedCount, setEstimatedCount] = useState<number | null>(null);
  const [sports, setSports] = useState<Array<{ value: string; label: string }>>([]);
  const [genderOptions, setGenderOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [membershipOptions, setMembershipOptions] = useState<Array<{ value: string; label: string }>>([]);
  
  const [formData, setFormData] = useState({
    segment_name: "",
    description: "",
    gender: [] as string[],
    sports: [] as string[],
    states: [] as string[],
    membership_types: [] as string[],
    explicit_profile_ids: null as string[] | null,
    is_active: true,
    audience_type: "athlete" as string,
    coach_roles: [] as string[],
  });

  // Initialize gender options (fixed to Men/Women only)
  useEffect(() => {
    setGenderOptions([
      { value: "Men", label: "Men" },
      { value: "Women", label: "Women" }
    ]);
  }, []);

  // Fetch memberships from database
  useEffect(() => {
    const fetchMemberships = async () => {
      const { data: membershipsData } = await supabase
        .from("memberships")
        .select("slug, name")
        .order("name");
      
      if (membershipsData) {
        setMembershipOptions(
          membershipsData.map(m => ({ 
            value: m.slug, 
            label: m.name 
          }))
        );
      }
    };
    
    fetchMemberships();
  }, []);

  // Fetch sports based on selected gender
  useEffect(() => {
    const fetchSports = async () => {
      if (formData.gender.length === 0) {
        // No gender selected, don't load sports
        setSports([]);
        return;
      }

      // Fetch sports for selected genders
      const { data: sportsData } = await supabase
        .from("sports")
        .select("id, name, gender")
        .in("gender", formData.gender)
        .order("name");
      
      if (sportsData) {
        setSports(sportsData.map(s => ({ value: s.id, label: s.name })));
        console.log("Loaded sports for gender(s):", formData.gender, sportsData.length);
      }
    };
    
    fetchSports();
  }, [formData.gender]);

  useEffect(() => {
    if (segment) {
      // Ensure all array fields are actual arrays, never null/undefined
      const ensureArray = (value: any): string[] => {
        if (Array.isArray(value)) return value;
        if (value === null || value === undefined) return [];
        return [];
      };

      // Normalize gender values from database to UI format
      const normalizeGender = (genderArray: any): string[] => {
        const genders = ensureArray(genderArray);
        return genders.map(g => {
          const lower = g?.toLowerCase();
          if (lower === 'male' || lower === 'men') return 'Men';
          if (lower === 'female' || lower === 'women') return 'Women';
          return g;
        }).filter(Boolean);
      };

      setFormData({
        segment_name: segment.segment_name,
        description: segment.description || "",
        gender: normalizeGender(segment.gender),
        sports: ensureArray(segment.sports),
        states: ensureArray(segment.states),
        membership_types: ensureArray(segment.membership_types),
        explicit_profile_ids: segment.explicit_profile_ids || null,
        is_active: segment.is_active,
        audience_type: segment.audience_type || "athlete",
        coach_roles: ensureArray(segment.coach_roles),
      });
      setEstimatedCount(segment.estimated_recipients);
    }
  }, [segment]);

  const handleCalculate = async () => {
    setIsCalculating(true);
    try {
      const count = await calculateRecipients({
        gender: formData.gender.length ? formData.gender : undefined,
        sports: formData.sports.length ? formData.sports : undefined,
        states: formData.states.length ? formData.states : undefined,
        membership_types: formData.membership_types.length ? formData.membership_types : undefined,
        audience_type: formData.audience_type,
      });
      setEstimatedCount(count);
      
      if (count === 0) {
        toast({
          title: `No matching ${formData.audience_type === "coach" ? "coaches" : "athletes"}`,
          description: "Try broadening your filters. Check console for details.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Calculation complete",
          description: `Found ${count.toLocaleString()} matching ${formData.audience_type === "coach" ? "coaches" : "athletes"}`,
        });
      }
    } catch (error) {
      toast({
        title: "Calculation failed",
        description: "Could not calculate recipients",
        variant: "destructive",
      });
    } finally {
      setIsCalculating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.segment_name.trim()) {
      toast({
        title: "Validation error",
        description: "Segment name is required",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const dataToSave = {
        ...formData,
        grad_year_min: null,
        grad_year_max: null,
        estimated_recipients: estimatedCount,
        last_count_updated: estimatedCount !== null ? new Date().toISOString() : null,
      };

      if (segment) {
        await updateSegment(segment.id, dataToSave);
        toast({
          title: "Segment updated",
          description: "Audience segment updated successfully",
        });
      } else {
        await createSegment(dataToSave);
        toast({
          title: "Segment created",
          description: "Audience segment created successfully",
        });
      }
      
      onSuccess();
      onBack();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save segment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const stateOptions = US_STATES.map(s => ({ value: s, label: s }));

  return (
    <div className="space-y-4">
      <Button variant="ghost" onClick={onBack} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Segments
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>{segment ? "Edit" : "Create"} Audience Segment</CardTitle>
          <p className="text-sm text-muted-foreground">
            Define the audience filters for targeted email campaigns
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="segment_name">Segment Name *</Label>
                <Input
                  id="segment_name"
                  value={formData.segment_name}
                  onChange={(e) => setFormData({ ...formData, segment_name: e.target.value })}
                  placeholder="e.g., Football Players 2025-2026"
                  required
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this audience segment"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Audience Type *</Label>
                <Select
                  value={formData.audience_type}
                  onValueChange={(value) => setFormData({ ...formData, audience_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select audience type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="athlete">Athletes</SelectItem>
                    <SelectItem value="coach">Coaches</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Gender</Label>
                <CheckboxDropdown
                  options={genderOptions}
                  selected={formData.gender}
                  onChange={(gender) => {
                    console.log("Gender changed to:", gender);
                    setFormData({ ...formData, gender, sports: [] }); // Clear sports when gender changes
                  }}
                  placeholder="Select gender..."
                />
              </div>

              <div className="space-y-2">
                <Label>Sports</Label>
                <CheckboxDropdown
                  options={sports}
                  selected={formData.sports}
                  onChange={(sports) => {
                    console.log("Sports changed to:", sports);
                    setFormData({ ...formData, sports });
                  }}
                  placeholder={formData.gender.length === 0 ? "Select gender first..." : "Select sports..."}
                  disabled={formData.gender.length === 0}
                />
              </div>

              <div className="space-y-2">
                <Label>States</Label>
                <CheckboxDropdown
                  options={stateOptions}
                  selected={formData.states}
                  onChange={(states) => {
                    console.log("States changed to:", states);
                    setFormData({ ...formData, states });
                  }}
                  placeholder="Select states..."
                />
              </div>

              <div className="space-y-2">
                <Label>Membership Types</Label>
                <CheckboxDropdown
                  options={membershipOptions}
                  selected={formData.membership_types}
                  onChange={(membership_types) => {
                    console.log("Membership types changed to:", membership_types);
                    setFormData({ ...formData, membership_types });
                  }}
                  placeholder="Select membership types..."
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div className="space-y-1">
                <Label>Estimated Recipients</Label>
                <p className="text-2xl font-bold">
                  {estimatedCount !== null ? estimatedCount.toLocaleString() : "—"}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleCalculate}
                disabled={isCalculating}
              >
                {isCalculating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Calculating...
                  </>
                ) : (
                  <>
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate Recipients
                  </>
                )}
              </Button>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>

            <div className="flex justify-end space-x-2 pt-6 border-t">
              <Button type="button" variant="outline" onClick={onBack}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  segment ? "Update Segment" : "Create Segment"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
